import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './styles.module.scss';

// Check if we're on mobile
const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPad|iPhone|iPod|android/i.test(userAgent);
};

// Email validation
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// 6-digit numeric code
const isValidTwoFaCode = (code) => /^\d{6}$/.test(code);

// Hide/show chat widget functions
const hideChatWidget = () => {
    if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.remove();
    }
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.remove();
        }
    });
    const style = document.createElement('style');
    style.id = 'hide-chat-widget-style';
    style.textContent = `#hubspot-messages-iframe-container { display: none !important; visibility: hidden !important; }`;
    document.head.appendChild(style);
};

const showChatWidget = () => {
    if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.load();
    }
    const style = document.getElementById('hide-chat-widget-style');
    if (style) {
        style.remove();
    }
};

const MobileTwoFaPanel = ({ twoFaEmail, twoFaError, twoFaChallengeId, verifyTwoFa, cancelTwoFa }) => {
    const [code, setCode] = useState('');
    const [localError, setLocalError] = useState('');

    const handleSubmit = () => {
        if (!isValidTwoFaCode(code)) {
            setLocalError('Enter the 6-digit code');
            return;
        }
        setLocalError('');
        verifyTwoFa(twoFaChallengeId, code);
    };

    return (
        <div className={styles.mobileContainer}>
            <div className={styles.mobileHeader}>
                <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                <h1 className={styles.mobileTitle}>Check your<br />email</h1>
            </div>
            <div className={styles.mobileContent}>
                <p className={styles.twoFaHelper}>
                    We sent a 6-digit code to <strong>{twoFaEmail}</strong>. Enter it below to finish signing in.
                </p>
                <div className={styles.mobileInputGroup}>
                    <div className={styles.mobileLabelRow}>
                        <label className={styles.mobileLabel}>Verification code</label>
                        {(localError || twoFaError) && (
                            <span className={styles.mobileError}>{localError || twoFaError}</span>
                        )}
                    </div>
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        className={styles.mobileInput}
                        placeholder="000000"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setLocalError('');
                        }}
                        autoFocus
                    />
                </div>
                <button
                    className={styles.mobileBorderedButton}
                    onClick={handleSubmit}
                    disabled={!isValidTwoFaCode(code)}
                >
                    <span>VERIFY</span>
                    <span className={styles.arrow}>→</span>
                </button>
                <button
                    type="button"
                    className={styles.mobileUnderlineButton}
                    onClick={cancelTwoFa}
                >
                    Back to login
                </button>
            </div>
        </div>
    );
};

// Mobile Login Component
const MobileLogin = (props) => {
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const location = useLocation();
    const loginTimeoutRef = useRef(null);

    useEffect(() => {
        hideChatWidget();

        if (props.urlParams) {
            const params = new URLSearchParams(props.urlParams);
            const emailParam = params.get('email');
            if (emailParam && (!props.email || props.email === "") && props.updateEmail) {
                props.updateEmail(emailParam);
            }
        }

        if (props.athleteInvite) {
            localStorage.setItem('pendingInviteCode', props.athleteInvite);
        }

        if (props.staffInvite) {
            localStorage.setItem('pendingStaffInviteCode', props.staffInvite);
        }

        return () => {
            showChatWidget();
            if (loginTimeoutRef.current) {
                clearTimeout(loginTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (props.twoFaChallengeId) {
            setIsLoggingIn(false);
            if (loginTimeoutRef.current) {
                clearTimeout(loginTimeoutRef.current);
            }
        }
    }, [props.twoFaChallengeId]);

    if (props.twoFaChallengeId) {
        return <MobileTwoFaPanel {...props} />;
    }

    const handleLogin = () => {
        let hasError = false;

        // Validate email
        if (!props.email) {
            setEmailError('Not a valid email');
            hasError = true;
        } else if (!isValidEmail(props.email)) {
            setEmailError('Not a valid email');
            hasError = true;
        } else {
            setEmailError('');
        }

        // Validate password
        if (!props.password || props.password.length < 8) {
            setPasswordError('At least 8 characters');
            hasError = true;
        } else {
            setPasswordError('');
        }

        if (hasError) return;

        // Clear any existing timeout before starting a new login attempt
        if (loginTimeoutRef.current) {
            clearTimeout(loginTimeoutRef.current);
        }

        setIsLoggingIn(true);
        props.signIn(props.email, props.password);

        // Reset logging in state after a delay (in case of error)
        loginTimeoutRef.current = setTimeout(() => setIsLoggingIn(false), 5000);
    };

    // Get redirect path for signup link
    const getSignupLink = () => {
        const params = new URLSearchParams(location.search);
        const redirect = params.get('redirect');
        if (redirect) {
            return `/signup?redirect=${encodeURIComponent(redirect)}`;
        }
        return '/signup';
    };

    return (
        <div className={styles.mobileContainer}>
            <div className={styles.mobileHeader}>
                <img src="https://assets.reponestrength.com/white_logo.png" className={styles.mobileLogo} alt="RepOne" />
                <h1 className={styles.mobileTitle}>Log in to your<br />RepOne account</h1>
            </div>
            <div className={styles.mobileContent}>
                <div className={styles.mobileInputGroup}>
                    <div className={styles.mobileLabelRow}>
                        <label className={styles.mobileLabel}>Email</label>
                        {emailError && <span className={styles.mobileError}>{emailError}</span>}
                    </div>
                    <input
                        type="email"
                        className={styles.mobileInput}
                        placeholder="name@domain.com"
                        value={props.email}
                        onChange={(e) => {
                            props.updateEmail(e.target.value);
                            setEmailError('');
                        }}
                        autoFocus
                        autoComplete="email"
                        autoCapitalize="off"
                        autoCorrect="off"
                    />
                </div>
                <div className={styles.mobileInputGroup}>
                    <div className={styles.mobileLabelRow}>
                        <label className={styles.mobileLabel}>Password</label>
                        {passwordError && <span className={styles.mobileError}>{passwordError}</span>}
                    </div>
                    <input
                        type="password"
                        className={styles.mobileInput}
                        placeholder="********"
                        value={props.password}
                        onChange={(e) => {
                            props.updatePassword(e.target.value);
                            setPasswordError('');
                        }}
                        autoComplete="current-password"
                    />
                </div>
                <button
                    className={styles.mobileBorderedButton}
                    onClick={handleLogin}
                    disabled={isLoggingIn}
                >
                    <span>{isLoggingIn ? 'LOGGING IN...' : 'LOG IN'}</span>
                    {isLoggingIn ? (
                        <img src="https://assets.reponestrength.com/white_logo.png" className={styles.spinnerIcon} alt="" />
                    ) : (
                        <span className={styles.arrow}>→</span>
                    )}
                </button>
                <Link to={getSignupLink()} className={styles.mobileUnderlineButton}>
                    Create a new account →
                </Link>
                <Link to="/forgotpw" className={styles.mobileUnderlineButton}>
                    Reset your password
                </Link>
            </div>
        </div>
    );
};

const DesktopTwoFaPanel = ({ twoFaEmail, twoFaError, twoFaChallengeId, verifyTwoFa, cancelTwoFa }) => {
    const [code, setCode] = useState('');
    const [localError, setLocalError] = useState('');

    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!isValidTwoFaCode(code)) {
            setLocalError('Enter the 6-digit code');
            return;
        }
        setLocalError('');
        verifyTwoFa(twoFaChallengeId, code);
    };

    return (
        <div>
            <div className={styles.main}>
                <img src="https://assets.reponestrength.com/black_logo.png" alt="RepOne" />
                <div>Verify your email</div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <p className={styles.twoFaHelper}>
                        We sent a 6-digit code to <strong>{twoFaEmail}</strong>. Enter it below to finish signing in.
                    </p>
                    <label>Verification code</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        className={styles.twoFaCodeInput}
                        placeholder="000000"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setLocalError('');
                        }}
                        autoFocus
                    />
                    {(localError || twoFaError) && (
                        <div className={styles.twoFaError}>{localError || twoFaError}</div>
                    )}
                    <div className={styles.buttonContainer}>
                        <input
                            className={styles.submitSideBySide}
                            type="submit"
                            value="Verify"
                            disabled={!isValidTwoFaCode(code)}
                        />
                        <button
                            type="button"
                            className={styles.createAccountButtonSideBySide}
                            onClick={cancelTwoFa}
                        >
                            Back
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Desktop Login Component (original)
const DesktopLogin = (props) => {
    useEffect(() => {
        hideChatWidget();

        if (props.urlParams) {
            const params = new URLSearchParams(props.urlParams);
            const emailParam = params.get('email');
            if (emailParam && (!props.email || props.email === "") && props.updateEmail) {
                props.updateEmail(emailParam);
            }
        }

        if (props.athleteInvite) {
            localStorage.setItem('pendingInviteCode', props.athleteInvite);
        }

        if (props.staffInvite) {
            localStorage.setItem('pendingStaffInviteCode', props.staffInvite);
        }

        return () => {
            showChatWidget();
        };
    }, []);

    if (props.twoFaChallengeId) {
        return <DesktopTwoFaPanel {...props} />;
    }

    return (
        <div>
            <div className={styles.main}>
                <img src="https://assets.reponestrength.com/black_logo.png" />
                <div>Log in to RepOne</div>

                <form className={styles.form} autoComplete={"on"} onSubmit={e => { e.preventDefault() }}>
                    <label>Email address</label>
                    <input type="text"
                        className={styles.textInput}
                        placeholder="name@domain.com"
                        value={props.email}
                        onChange={(event) => props.updateEmail(event.target.value)} />
                    <label>Password</label>
                    <input type="password"
                        className={styles.textInput}
                        placeholder="password"
                        value={props.password}
                        onChange={(event) => props.updatePassword(event.target.value)} />
                    <div className={styles.link}><Link to='/forgotpw'>Forgot password</Link></div>

                    {props.staffInvite ? (
                        <div className={styles.buttonContainer}>
                            <input className={styles.submitSideBySide} type="submit" value="Log in" onClick={() => props.signIn(props.email, props.password)} />
                            <Link
                                to={`/staffSignup?staff_invite=${props.staffInvite}`}
                                className={styles.createAccountButtonSideBySide}
                            >
                                Create account
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.buttonContainer}>
                            <input className={styles.submitSideBySide} type="submit" value="Log in" onClick={() => props.signIn(props.email, props.password)} />
                            <Link
                                to={`/beta`}
                                className={styles.createAccountButtonSideBySide}
                            >
                                Create account
                            </Link>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

// Main component that switches between mobile and desktop
export default props => {
    if (isMobile()) {
        return <MobileLogin {...props} />;
    }
    return <DesktopLogin {...props} />;
};
